import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchFilteredTenants, deleteTenant, Tenant, TenantFilterRequest } from './TenantApi';
import { TENANT_STATUSES } from '../../../constants/tenantStatus';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import AboutSection from '../../AboutSection';

const formatDate = (date?: string) =>
  date ? new Date(date).toLocaleString() : '-';

const TenantList: React.FC = () => {
  const navigate = useNavigate();
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [pagination, setPagination] = useState({ page: 0, size: 10, totalPages: 1, totalElements: 0 });
  const [sortConfig, setSortConfig] = useState({ field: 'createdAt', direction: 'desc' });

  const [filters, setFilters] = useState<TenantFilterRequest>({
    name: '',
    subdomain: '',
    contactEmail: '',
    mobileNo: '',
    whatsAppNo: '',
    status: [],
    dateRange: { from: '', to: '' },
    pagination: { page: 0, size: 10 },
    sort: { field: 'createdAt', direction: 'desc' },
  });

  const loadTenants = async () => {
    setLoading(true);
    try {
      const response = await fetchFilteredTenants(filters);
      const data = response.data;
      setTenants(data.content);
      setPagination({
        page: data.number,
        size: data.size,
        totalPages: data.totalPages,
        totalElements: data.totalElements,
      });
    } catch (err) {
      setError('Failed to fetch tenants');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTenants();
  }, [filters]);

  const updateFilter = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFilters((prev) => ({
      ...prev,
      [name]: value,
      pagination: { ...prev.pagination, page: 0 },
    }));
  };

  const updateDateFilter = (key: 'from' | 'to', value: string) => {
    setFilters((prev) => ({
      ...prev,
      dateRange: { ...prev.dateRange, [key]: value },
      pagination: { ...prev.pagination, page: 0 },
    }));
  };

  const handlePageSizeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const size = parseInt(e.target.value, 10) || 1;
    setFilters((prev) => ({
      ...prev,
      pagination: { ...prev.pagination, size, page: 0 },
    }));
  };

  const changePage = (newPage: number) => {
    setFilters((prev) => ({
      ...prev,
      pagination: { ...prev.pagination, page: newPage },
    }));
  };

  const handleSort = (field: string) => {
    const direction = sortConfig.field === field && sortConfig.direction === 'asc' ? 'desc' : 'asc';
    setSortConfig({ field, direction });
    setFilters((prev) => ({ ...prev, sort: { field, direction } }));
  };

  const handleDelete = async (id: string) => {
    if (confirm('Are you sure you want to delete this tenant?')) {
      await deleteTenant(id);
      loadTenants();
    }
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold">Tenants</h2>
        <button
          onClick={() => navigate('create')}
          className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
        >
          Create Tenant
        </button>
      </div>

      <div className="bg-gray-100 p-4 rounded mb-2">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-4">
          {['name', 'subdomain', 'contactEmail', 'mobileNo', 'whatsAppNo'].map((field) => (
            <div key={field}>
              <label className="text-sm font-medium mb-1 block">
                {field.charAt(0).toUpperCase() + field.slice(1)}
              </label>
              <input
                type="text"
                name={field}
                placeholder={`Enter ${field}`}
                value={(filters as any)[field]}
                onChange={updateFilter}
                className="border px-3 py-2 rounded w-full"
              />
            </div>
          ))}

          <div>
            <label className="text-sm font-medium mb-1 block">Status</label>
            <select
              name="status"
              multiple
              value={filters.status}
              onChange={(e) =>
                setFilters((prev) => ({
                  ...prev,
                  status: Array.from(e.target.selectedOptions, (opt) => opt.value),
                  pagination: { ...prev.pagination, page: 0 },
                }))
              }
              className="border px-3 py-2 rounded w-full h-[96px] truncate"
            >
              {TENANT_STATUSES.map((s) => (
                <option key={s} value={s} title={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-sm font-medium mb-1 block">From Date</label>
            <input
              type="date"
              value={filters.dateRange?.from?.split('T')[0] || ''}
              onChange={(e) => updateDateFilter('from', `${e.target.value}T00:00:00Z`)}
              className="border px-3 py-2 rounded w-full"
            />
          </div>

          <div>
            <label className="text-sm font-medium mb-1 block">To Date</label>
            <input
              type="date"
              value={filters.dateRange?.to?.split('T')[0] || ''}
              onChange={(e) => updateDateFilter('to', `${e.target.value}T23:59:59Z`)}
              className="border px-3 py-2 rounded w-full"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="text-sm font-medium mb-1 block">Page Size</label>
            <input
              type="number"
              min={1}
              value={filters.pagination.size}
              onChange={handlePageSizeChange}
              className="border px-2 py-1 rounded w-full max-w-[100px]"
            />
          </div>
          <div className="md:col-span-2 flex items-end">
            <button
              onClick={loadTenants}
              className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
            >
              Apply Filters
            </button>
          </div>
        </div>
      </div>

      {loading ? (
        <div>Loading...</div>
      ) : error ? (
        <div className="text-red-500">{error}</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full table-auto border border-gray-300 text-sm">
            <thead className="bg-gray-200">
              <tr>
                {[
                  'name',
                  'subdomain',
                  'schemaName',
                  'contactEmail',
                  'mobileNo',
                  'whatsAppNo',
                  'status',
                  'comment',
                  'createdAt',
                  'updatedAt',
                ].map((field) => (
                  <th
                    key={field}
                    onClick={() => handleSort(field)}
                    className="border px-4 py-2 text-left font-semibold cursor-pointer hover:bg-gray-300"
                  >
                    {field.charAt(0).toUpperCase() + field.slice(1)}{' '}
                    {sortConfig.field === field ? (sortConfig.direction === 'asc' ? '▲' : '▼') : ''}
                  </th>
                ))}
                <th className="border px-4 py-2 text-left">Actions</th>
              </tr>
            </thead>
            <tbody>
              {tenants.map((tenant) => (
                <tr key={tenant.id} className="hover:bg-gray-50">
                  <td className="border px-4 py-2">{tenant.name}</td>
                  <td className="border px-4 py-2">{tenant.subdomain}</td>
                  <td className="border px-4 py-2">{tenant.schemaName}</td>
                  <td className="border px-4 py-2">{tenant.contactEmail}</td>
                  <td className="border px-4 py-2">{tenant.mobileNo}</td>
                  <td className="border px-4 py-2">{tenant.whatsAppNo}</td>
                  <td className="border px-4 py-2">{tenant.status}</td>
                  <td className="border px-4 py-2">{tenant.comment}</td>
                  <td className="border px-4 py-2">{formatDate(tenant.createdAt)}</td>
                  <td className="border px-4 py-2">{formatDate(tenant.updatedAt)}</td>
                  <td className="border px-4 py-2 flex gap-2">
                    <button
                      className="bg-blue-500 text-white px-2 py-1 rounded"
                      onClick={() => navigate(`/admin/tenants/${tenant.id}`)}
                    >
                      View
                    </button>
                    <button
                      className="bg-yellow-500 text-white px-2 py-1 rounded"
                      onClick={() => navigate(`/admin/tenants/${tenant.id}/edit`)}
                    >
                      Edit
                    </button>
                    <button
                      className="bg-red-600 text-white px-2 py-1 rounded"
                      onClick={() => handleDelete(tenant.id)}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="flex justify-between mt-4 items-center">
            <span>
              Showing page {pagination.page + 1} of {pagination.totalPages} — {pagination.totalElements} total records
            </span>
            <div className="flex gap-2">
              <button
                disabled={pagination.page === 0}
                onClick={() => changePage(pagination.page - 1)}
                className="px-3 py-1 bg-gray-300 rounded disabled:opacity-50"
              >
                Prev
              </button>
              <button
                disabled={pagination.page >= pagination.totalPages - 1}
                onClick={() => changePage(pagination.page + 1)}
                className="px-3 py-1 bg-gray-300 rounded disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
export default TenantList;

